"use client";

import Image from "next/image";
import Link from "next/link";
import CartIcon from "./CartIcon";
import MobileMenu from "./MobileMenu";
import { usePostHog } from "@/hooks/usePostHog";
import { useEffect, useState } from "react";
import CountdownTimer, { isHotSaleActive } from "./CountdownTimer";

interface HeaderNavProps {
  promoText: string;
  promoHref: string;
}

export default function HeaderNav({ promoText, promoHref }: HeaderNavProps) {
  const { track } = usePostHog();
  const [links, setLinks] = useState<Array<{label:string; href:string}>>([])

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/header-links')
        const data = await res.json().catch(() => [])
        setLinks(Array.isArray(data) ? data : [])
      } catch {}
    })()
  }, [])

  const handleNavClick = (section: string) => {
    track('navigation_clicked', {
      section,
      location: 'header',
      type: 'anchor_link'
    });
  };

  const handleCTAClick = (source: string, destination: string) => {
    track('cta_clicked', {
      cta_text: source === 'promo' ? promoText : 'COMPRA',
      destination,
      location: source === 'promo' ? 'promo_bar' : 'header',
      type: 'navigation'
    });
  };

  const handleLogoClick = () => {
    track('logo_clicked', {
      location: 'header',
      destination: 'homepage'
    });
  };

  const navLinks = links.filter(l => !!l.label && !!l.href);

  return (
    // `data-site-chrome` marks the whole fixed assembly — promo bar + nav — so
    // a page can hide the site's chrome with a stable selector instead of a
    // chain of utility classes that breaks the next time this div is restyled.
    // Used by /lp/[slug] (Atlas-published landing pages).
    //
    // Deliberately NOT named `data-promo-bar`: LPStyleOverride already exposes a
    // `hidePromoBar` option whose selector `[data-promo-bar]` matches nothing in
    // this codebase, so that option has never actually done anything. Using
    // that name here would silently switch it on for every live /oferta LP whose
    // config sets `hide_promo_bar: true` — a visible change to production pages,
    // which is not this branch's call to make.
    <div data-site-chrome className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
      {/* Promotional bar */}
      <div className="bg-black text-white">
        {isHotSaleActive() ? (
          <Link
            href="/comprar"
            onClick={() => {
              handleCTAClick('promo', '/comprar');
              track('hotsale_banner_click', { location: 'promo_bar' });
            }}
            className="flex items-center justify-center gap-2 w-full py-2 text-center text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer font-mono"
          >
            <span>HOT SALE: hasta 30% OFF</span>
            <span className="hidden sm:inline opacity-70">|</span>
            <CountdownTimer compact className="hidden sm:inline tabular-nums" />
          </Link>
        ) : (
          <Link
            href={promoHref || '/comprar'}
            onClick={() => handleCTAClick('promo', promoHref)}
            className="block w-full py-2 text-center text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer"
          >
            {promoText}
          </Link>
        )}
      </div>
      
      {/* Main header */}
      <header className="container-page py-3 flex items-center justify-between">
        <Link 
          href="/" 
          aria-label="Ir al inicio"
          onClick={handleLogoClick}
        >
          <Image src="/images/olin-labs-logo-final.svg" alt="Olin" width={120} height={72} priority />
        </Link>
        <nav className="flex items-center gap-4 text-sm font-mono">
          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-4">
            {navLinks.map((l, i) => (
              <span key={l.href || l.label || i} className="flex items-center gap-4">
                <Link href={l.href} className="hover:opacity-70" onClick={() => handleNavClick(l.label)}>{l.label}</Link>
                {i < navLinks.length - 1 && <span className="text-gray-400">|</span>}
              </span>
            ))}
          </div>
          {/* Comprar button always visible */}
          <Link 
            href="/comprar" 
            className="btn btn-primary font-mono px-4 py-2"
            onClick={() => handleCTAClick('header', '/comprar')}
          >
            COMPRA
          </Link>
          <CartIcon />
          {/* Mobile hamburger */}
          <div className="md:hidden">
            <MobileMenu />
          </div>
        </nav>
      </header>
    </div>
  );
}
